import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useHoverDirty, useClickAway } from 'react-use';
import { ExternalUrl } from '../../../constants/url.mjs';
import '../../../contexts/MenuContext/mapping.mjs';
import '../../../hooks/index.mjs';
import '../../StaticModal/index.mjs';
import { Frame, Avatar, NameWrap, Name, NamePopover, Email, ButtonWrap, CustomButton, CustomLinkBtn } from './AccountFrame.styles.mjs';
import './translations/index.mjs';
import { useTranslation } from '../../../hooks/useTranslation.mjs';
import { Language } from '../../../types/constants.mjs';
import EN_TRANSLATION from './translations/en.json.mjs';
import { StaticModal } from '../../StaticModal/StaticModal.mjs';
"use strict";
const AccountFrame = function AccountFrame2({
  open,
  onClose,
  renderImage,
  account
}) {
  const ref = useRef(null);
  const name = useRef(null);
  const {
    formatMessage
  } = useTranslation({
    defaultTranslation: EN_TRANSLATION,
    additionalTranslations: {
      [Language.Ar]: () => import('./translations/ar.json.mjs').then(res => res.default),
      [Language.Ca]: () => import('./translations/ca.json.mjs').then(res => res.default),
      [Language.Cs]: () => import('./translations/cs.json.mjs').then(res => res.default),
      [Language.Da]: () => import('./translations/da.json.mjs').then(res => res.default),
      [Language.Nl]: () => import('./translations/nl.json.mjs').then(res => res.default),
      [Language.Fr]: () => import('./translations/fr.json.mjs').then(res => res.default),
      [Language.De]: () => import('./translations/de.json.mjs').then(res => res.default),
      [Language.El]: () => import('./translations/el.json.mjs').then(res => res.default),
      [Language.It]: () => import('./translations/it.json.mjs').then(res => res.default),
      [Language.Ja]: () => import('./translations/ja.json.mjs').then(res => res.default),
      [Language.Ko]: () => import('./translations/ko.json.mjs').then(res => res.default),
      [Language.No]: () => import('./translations/no.json.mjs').then(res => res.default),
      [Language.Pl]: () => import('./translations/pl.json.mjs').then(res => res.default),
      [Language.PtPt]: () => import('./translations/pt-PT.json.mjs').then(res => res.default),
      [Language.EsMx]: () => import('./translations/es-MX.json.mjs').then(res => res.default),
      [Language.Sv]: () => import('./translations/sv.json.mjs').then(res => res.default),
      [Language.Tr]: () => import('./translations/tr.json.mjs').then(res => res.default),
      [Language.Zh]: () => import('./translations/zh-Hans.json.mjs').then(res => res.default),
      [Language.Tw]: () => import('./translations/zh-Hant-TW.json.mjs').then(res => res.default),
      [Language.PtBr]: () => import('./translations/pt-BR.json.mjs').then(res => res.default),
      [Language.Ru]: () => import('./translations/ru.json.mjs').then(res => res.default),
      [Language.EsEs]: () => import('./translations/es-ES.json.mjs').then(res => res.default)
    }
  });
  const isHovering = useHoverDirty(
  // Hack: Type issue with ref on useHoverDirty side - https://github.com/streamich/react-use/issues/2379
  name);
  const [showPopover, setShowPopover] = useState(false);
  useEffect(() => {
    if (name.current) {
      const displayPopover = name.current.offsetWidth < name.current.scrollWidth;
      if (displayPopover && isHovering) {
        setShowPopover(true);
      } else if (displayPopover && !isHovering) {
        setShowPopover(false);
      }
    }
  }, [isHovering]);
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);
  useClickAway(ref, event => {
    let canClose = true;
    if (event.target instanceof Element) {
      const oneTrustElId = "onetrust-banner-sdk";
      const isOneTrustEl = event.target.id === oneTrustElId || !!event.target.closest(`#${oneTrustElId}`);
      if (isOneTrustEl) {
        canClose = false;
      }
    }
    if (canClose) {
      handleClose();
    }
  }, ["click"]);
  const hasAccountEvents = useMemo(() => {
    return !!(account && (account.handleLogin || account.handleRegister || account.handleLogout));
  }, [account]);
  const handleLogout = useCallback(() => {
    account?.handleLogout?.();
    handleClose();
  }, [handleClose, account]);
  const handleUnifiSiteManager = useCallback(() => {
    window.open(ExternalUrl.UNIFI_SITE_MANAGER);
    handleClose();
  }, [handleClose]);
  const handleOrderHistoryClick = useCallback(() => {
    account?.handleOrderHistory?.();
    handleClose();
  }, [account, handleClose]);
  const onManageAccount = useCallback(() => {
    account?.handleManageAccount?.();
    handleClose();
  }, [account, handleClose]);
  return /* @__PURE__ */jsx(StaticModal, {
    show: open,
    width: 250,
    children: /* @__PURE__ */jsxs(Frame, {
      ref,
      children: [account?.customer && /* @__PURE__ */jsxs(Fragment, {
        children: [/* @__PURE__ */jsx(Avatar, {
          renderImage,
          src: account?.customer.picture,
          size: "medium"
        }), /* @__PURE__ */jsxs(NameWrap, {
          children: [/* @__PURE__ */jsx(Name, {
            ref: name,
            children: account?.customer.displayName
          }), /* @__PURE__ */jsx(NamePopover, {
            $show: showPopover,
            children: account?.customer.displayName
          })]
        }), /* @__PURE__ */jsx(Email, {
          children: account?.customer.email
        })]
      }), /* @__PURE__ */jsxs(ButtonWrap, {
        children: [account?.customer && account?.handleOrderHistory && /* @__PURE__ */jsx(CustomButton, {
          "data-test-id": "order-history-btn",
          variant: "primary",
          full: true,
          onClick: handleOrderHistoryClick,
          children: formatMessage("Order History")
        }), !account?.customer && account?.handleLogin && /* @__PURE__ */jsx(CustomButton, {
          "data-test-id": "log-in-btn",
          variant: "primary",
          full: true,
          onClick: () => account.handleLogin?.(),
          children: formatMessage("Log In")
        }), !account?.customer && account?.handleRegister && /* @__PURE__ */jsx(CustomLinkBtn, {
          "data-test-id": "sign-up-btn",
          variant: "link",
          onClick: () => account.handleRegister?.(),
          children: formatMessage("Sign Up")
        }), (!hasAccountEvents || account?.customer && !account?.handleManageAccount) && /* @__PURE__ */jsx(CustomButton, {
          anchor: true,
          variant: "primary",
          full: true,
          href: ExternalUrl.ACCOUNT,
          children: formatMessage("UI Account")
        }), /* @__PURE__ */jsx(CustomLinkBtn, {
          variant: "link",
          onClick: handleUnifiSiteManager,
          children: formatMessage("UniFi Site Manager")
        }), account?.customer && account.handleManageAccount && /* @__PURE__ */jsx(CustomLinkBtn, {
          variant: "link",
          onClick: onManageAccount,
          children: formatMessage("Manage Account")
        }), account?.customer && !!account.handleLogout && /* @__PURE__ */jsx(CustomLinkBtn, {
          "data-test-id": "sign-out-btn",
          variant: "link",
          onClick: handleLogout,
          children: formatMessage("Sign Out")
        })]
      }), account?.renderStorePicker?.(onClose)]
    })
  });
};
export { AccountFrame };
//# sourceMappingURL=AccountFrame.mjs.map
